import api from '@/lib/services/general.service'
import { gateActionsBehindLogin, loginForSave } from "@/lib/services/guest";
import { vanishedIds, returnedIds, orphanedTimelineIds } from '@/lib/ongoingSync'
import { confirmPath, rejectPath, acceptActionPath, dismissActionPath } from '@/lib/storyAutomation'
import { OFFERS_PATH, offerItems } from '@/lib/storyOffers'
import { emailSaveItem } from '@/lib/researchEmailItem'
import { packSelection } from '@/lib/packSelection'
import { photoThumbnail } from '@/lib/photoFallback'

// Guests run the SAME server flow: every request carries X-Guest-Id, the BFF derives a namespaced
// guest user id from it, and downstream code is identical to signed-in. The BFF caps guests at a
// couple of ongoings — a 403 GUEST_LIMIT means "time to sign in".
const isGuestLimit = (e) => e?.response?.status === 403 && e?.response?.data?.code === 'GUEST_LIMIT'

// The suggestions read in flight, if any — see fetchSuggestions.
let suggestionsInFlight = null

// How long to wait before the second reconcile after an entity delete/restore — the change travels to
// timeline-srv over the service bus, so the first (immediate) read can still see the old membership.
const ENTITY_SYNC_RETRY_MS = 1500

// Where the sidebar's pinned ongoing is remembered (#206). Storage, not the server: "the one I am
// working on" is a property of this browser, and the ask is explicitly that it survives a reload —
// no ongoing field was added for it. Both helpers swallow their errors: storage can be blocked
// (private mode, an embedded webview) and a sidebar ordering must never be the thing that throws.
const PINNED_KEY = 'busy.ongoing.pinned'

export function readPinnedId() {
  try { return localStorage.getItem(PINNED_KEY) || null } catch { return null }
}

export function writePinnedId(id) {
  try {
    if (id) localStorage.setItem(PINNED_KEY, id)
    else localStorage.removeItem(PINNED_KEY)
  } catch { /* storage blocked — the pin is then session-only */ }
}

// ── Research notes → real notes ─────────────────────────────────────────────────────────────────
// A text piece saved from research is a NOTE: it gets a proper short title (first sentence, cut at
// a word boundary) and is ALSO created in the Notes module (DB) so it behaves like any other note
// (listed, searchable, ready for drag & drop). The rest of the text stays as its content.

const noteEsc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

function researchNoteTitle(item, query) {
  const text = String(item?.title || item?.content || '')
    .replace(/[#*_`>]+/g, '').replace(/\s+/g, ' ').trim()
  const sentence = (text.split(/(?<=[.!?])\s/)[0] || text).trim()
  let title = sentence.length > 60 ? `${sentence.slice(0, 60).replace(/\s+\S*$/, '')}…` : sentence
  if (!title || title.length < 4) title = query ? `Note — ${query}` : 'Research note'
  return title
}

/**
 * One Work-board row → the research item the save sends.
 *
 * A ticked EMAIL is its own shape (busy-timeline-srv#35): it travels as `kind: 'email'` with the
 * thread id, which is what makes timeline-srv write the story membership off the save. Everything
 * else is the mapping the three save paths have always used, in one place now, because they each
 * had their own copy of it and the email branch must not reach only one of them.
 */
function researchSaveItem(item, query) {
  return emailSaveItem(item) || {
    kind: item.kind,
    query: item.query,
    // Note items get a proper short title (not a raw text slice) — same title as their real note.
    title: item.kind === 'note' ? researchNoteTitle(item, item.query || query) : item.title,
    url: item.url,
    content: researchSaveContent(item),
  }
}

// A PHOTO's content is its thumbnail (busy-my-web#691). The research item has no thumbnail field
// and a photo's content was always empty, so the search engine's small thumbnail — the picture the
// user actually saw in the photo row — is kept there, and `url` stays the full picture. Many sites
// hotlink-block their full images, so without it a saved photo had nothing loadable to show. An
// inline (data:) photo keeps its own content; every other kind is untouched.
function researchSaveContent(item) {
  if (item.kind !== 'photo') return item.content
  return photoThumbnail(item) || item.content
}

const researchSaveItems = (items, query) => (items || []).map(i => researchSaveItem(i, query))

// Fire-and-forget: create the real notes; a failure never blocks the research save itself.
function persistResearchNotes(items, query) {
  const notes = (items || []).filter(i => i.kind === 'note')
  if (!notes.length) return
  Promise.all(notes.map((n) => {
    const text = String(n.content || n.md || n.title || '').trim()
    const content = `<p>${noteEsc(text)}</p>${query ? `<p><em>From research: ${noteEsc(query)}</em></p>` : ''}`
    return api.insert('note', {
      title: researchNoteTitle(n, query),
      content,
      isImportant: false,
      isPreset: false,
      // Research-born: a real note (openable, drag & drop) but NOT shown in the Notes module —
      // only notes created there (form / SmartBox note input) appear in its listings.
      isResearch: true,
    }).catch(() => null)
  })).catch(() => null)
}

// "Ongoing" = a story family. Status is derived on the server (overdue → dueSoon → waiting → active
// → completed). Auto-created when research is saved (Rule 4).
export const state = {
  // Stage 8 "filter panel": what the middle Story shows. `kind` = show ONLY that step kind
  // (null = the normal story); packId narrows to one Research Pack. Reset when a story opens.
  storyFilters: { kind: null, packId: null },
  // The story whose page is COVERED right now — its route is still open, but the shell is drawing
  // something else in its slot (a search, an email read in place). Its current story and its filters
  // are kept for its return (Pouria, 2026-10-01; lib/storyCover.js).
  coveredStoryId: null,
  // Focused pack's main middle view — switched from the SmartBox strip: 'doc' (Summary, the
  // default when a pack opens) | 'story'.
  packView: 'doc',
  // The open pack's selected item, `{ packId, itemId }` | null (busy-my-web#605, Saffet 2026-10-09):
  // ONE answer for both sides — a rail card tapped and an extraction clicked in the document write
  // it, the rail card and the document read it. Shape and rules: lib/packSelection.js.
  packSelection: null,
  items: [],
  loaded: false,
  // The Story currently open (/ongoing/:id) — the right panel shows its Timeline.
  currentStory: null,
  // Stories already read this session, by id (#211). `clearCurrentStory` fires on every leave, so
  // without this every re-open blanked the page and paid the full `{id}/story` round trip again for
  // an answer the browser had held moments before. Kept as the LAST KNOWN state of each story —
  // the optimistic patches below write through it — so a re-open never shows an edit being undone.
  storyCache: {},
  // Smart Inbox: pending "start an ongoing from this email?" suggestions awaiting confirm/dismiss.
  suggestions: [],
  // The OPEN story's own offers (busy-my-web#291): the threads it has been suggested and not yet
  // given, as rows for its "Connect this email to this Ongoing" block. Kept beside `suggestions`
  // rather than inside it because they are read from a different endpoint about ONE story — but the
  // ids are the same relationship ids, so answering an offer clears it from BOTH lists (see
  // `addSuggestedThread` below). Emptied whenever a story is opened, so one story's offers are never
  // drawn over another's.
  offers: [],
  // Recently-deleted ids [{ id, at }] — suppressed from the list even if a stale cached fetch still
  // returns them (guards against the read-through cache race on delete), forgotten after 90s.
  deletedIds: [],
  // The ongoing last created OR last opened, pinned to the top of the sidebar
  // list until a DIFFERENT one is opened (#33, refined by #39, then #206).
  //
  // #39 released the pin the moment the story was opened, on the reasoning that
  // the pin only existed to help you find what you had just made. Saffet's ask
  // is the other half of the same idea: the one you are working on is the one
  // you last opened, and it should stay reachable at the top rather than sink
  // back into priority order the instant you read it. So opening MOVES the pin
  // instead of dropping it, and the pin only ever leaves for another ongoing.
  //
  // Persisted (localStorage, `PINNED_KEY`) because "until I open another one"
  // outlives a reload — a browser-local preference, not a new backend field.
  recentId: readPinnedId(),
}

export const getters = {
  recentId: s => s.recentId,
  storyFilters: s => s.storyFilters,
  coveredStoryId: s => s.coveredStoryId,
  packView: s => s.packView,
  packSelection: s => s.packSelection,
  items: s => s.items,
  loaded: s => s.loaded,
  currentStory: s => s.currentStory,
  cachedStory: s => id => (id && s.storyCache[id]) || null,
  suggestions: s => s.suggestions,
  offers: s => s.offers,
  byId: s => id => s.items.find(o => o.id === id) || null,
  // Sub-ongoings of a given parent.
  childrenOf: s => id => s.items.filter(o => o.parentOngoingId === id),
  // Top-level ongoings (no parent) — for the flat/tree list.
  roots: s => s.items.filter(o => !o.parentOngoingId),
}

export const actions = {
  // Stage 8: the lower Context Panel sets these; the middle Story applies them.
  setStoryFilters({ commit }, patch) { commit('SET_STORY_FILTERS', patch) },
  setPackView({ commit }, view) {
    commit('SET_PACK_VIEW', ['doc', 'story', 'notes', 'links', 'images'].includes(view) ? view : 'doc')
  },
  resetStoryFilters({ commit }) { commit('RESET_STORY_FILTERS') },
  // Select (`{ packId, itemId }`) or clear (null / no ids) the open pack's item — lib/packSelection.js.
  selectPackItem({ commit }, sel) { commit('SET_PACK_SELECTION', packSelection(sel?.packId, sel?.itemId)) },
  // Record (id) or forget (null) the covered story — see lib/storyCover.js.
  coverStory({ commit }, id) { commit('SET_COVERED_STORY', id || null) },
  async fetch({ commit }) {
    try {
      const res = await api.get('ongoing', {})
      commit('SET_ITEMS', Array.isArray(res?.data) ? res.data : [])
    } catch {
      commit('SET_ITEMS', [])
    } finally {
      commit('SET_LOADED', true)
    }
  },

  // A task / note / event / email was deleted or restored. The server derives "this ongoing has
  // nothing left in it" from its members, so the ongoing that held it may have just been hidden — or
  // brought back — WITHOUT any ongoing endpoint being called (nothing bumped the BFF list version).
  // Re-read past the cache and reconcile: tombstone what vanished (same 90s guard `remove` uses, so a
  // stale in-flight fetch can't resurrect it) and forget the tombstone of anything that came back.
  async reconcile({ commit, state }) {
    const before = state.items.map(o => o.id)
    let list
    try {
      const res = await api.get('ongoing', { refresh: true })
      list = Array.isArray(res?.data) ? res.data : []
    } catch {
      return // leave the list as it is — a failed refresh must not empty the sidebar
    }
    commit('FORGET_DELETED', returnedIds(state.deletedIds, list))
    commit('SET_ITEMS', list)
    commit('TOMBSTONE_ITEMS', vanishedIds(before, list))
  },

  // The entity delete/restore reaches timeline-srv over the service bus, so the ongoing's membership
  // changes a moment AFTER the HTTP call returns. Reconcile straight away (usually enough) and once
  // more shortly after, so a slower bus still lands without the user touching refresh.
  async syncAfterEntityChange({ dispatch }) {
    await dispatch('reconcile')
    setTimeout(() => dispatch('reconcile'), ENTITY_SYNC_RETRY_MS)
  },

  // Append more saved research to an existing Story (continue searching from it). `packId` keeps the
  // save inside the CURRENT research pack; without it a NEW pack (fresh research thread) is started.
  // The pack's title follows the latest search.
  async addResearch({ dispatch, commit, rootGetters }, { id, items, packId, packTitle }) {
    // Label the save event with the step's exact search so the timeline reads specifically.
    const stepQuery = (items || []).find(i => i.query)?.query
    const payload = {
      kind: 'research',
      label: stepQuery ? `Your search: ${stepQuery}` : `Saved ${items.length} research item${items.length === 1 ? '' : 's'}`,
      researchItems: researchSaveItems(items, stepQuery),
      packId: packId || undefined,
      newPack: !packId,
      // The title names the pack ONLY at creation — later saves never rename it. A user-typed name
      // (the "Create new Research Pack" flow) wins over the search query.
      packTitle: !packId ? (packTitle || stepQuery || undefined) : undefined,
    }
    // Guests skip the note MIRROR only (their 2-note taste limit would bounce the save into
    // sign-in) — the research items themselves save server-side exactly like signed-in.
    if (rootGetters['user/isAuthenticated']) persistResearchNotes(items, stepQuery)
    const res = await api.insertWithPath('ongoing', `${id}/advance`, payload)
    const data = res?.data ?? null
    if (data) commit('SET_CURRENT_STORY', data)
    await dispatch('fetch')
    return data
  },

  /**
   * The same save, into SEVERAL packs (busy-ai-srv#14).
   *
   * One call: the BFF fans it out to the advance endpoint `addResearch` above uses, once per
   * destination, so a two-pack save is two proven writes rather than a new kind of write. The items
   * are shaped exactly as `addResearch` shapes them — same note titles, same `query` provenance on
   * every item — because a finding must say which search found it in the third pack as well as the
   * first.
   *
   * Answers { story, saved: [{ id, title }], failed: [...] }: the notice names the packs the save
   * actually reached, and a pack that refused is reported rather than quietly dropped.
   */
  async addResearchMulti({ dispatch, commit, rootGetters }, { id, items, packIds, newPackTitle }) {
    const stepQuery = (items || []).find(i => i.query)?.query
    const payload = {
      packIds: (packIds || []).filter(Boolean),
      newPackTitle: newPackTitle || undefined,
      items: researchSaveItems(items, stepQuery),
    }
    // Same guest rule as the single-pack save: the note MIRROR is the signed-in half.
    if (rootGetters['user/isAuthenticated']) persistResearchNotes(items, stepQuery)
    const res = await api.insertWithPath('ongoing', `${id}/research/save-multi`, payload)
    const data = res?.data ?? null
    if (data?.story) commit('SET_CURRENT_STORY', data.story)
    await dispatch('fetch')
    return data
  },

  // One lifecycle step on a Story (States 5–10). Returns the refreshed story.
  async advance({ dispatch, commit }, { id, payload }) {
    const res = await api.insertWithPath('ongoing', `${id}/advance`, payload)
    const data = res?.data ?? null
    if (data) commit('SET_CURRENT_STORY', data)
    await dispatch('fetch') // sidebar status may have changed
    return data
  },

  // Attach an email thread (timeline) to an ongoing — so a reply / forward / new email sent from
  // within the ongoing becomes part of it. Idempotent server-side (re-linking the same thread is a
  // no-op), so threaded replies (which stay in the already-linked thread) are safe to call with.
  async linkTimeline(_ctx, { id, timelineId }) {
    if (!id || !timelineId) return false
    try {
      await api.insertWithPath('ongoing', `${id}/timelines`, { timelineId, isSuggested: false })
      return true
    } catch {
      return false
    }
  },

  // …and its undo (chat rebuild integration: the chat's Connect → Undo, frame 21). The BFF's
  // UnlinkTimeline, `DELETE v1/ongoing/{id}/timelines/{timelineId}`. Answers true when it landed.
  async unlinkTimeline(_ctx, { id, timelineId } = {}) {
    if (!id || !timelineId) return false
    try {
      await api.deleteWithPath('ongoing', id, `timelines/${timelineId}`)
      return true
    } catch {
      return false
    }
  },

  // The full Story view for one Ongoing (status + story + focus + research + timeline).
  //
  // This is where "opened" happens, so it is where the top-of-list pin MOVES
  // (#206): the ongoing you just opened is the one you are working on, and it
  // holds the top until you open another. Taken on the attempt rather than on
  // success: a story that failed to load has still been opened as far as the
  // user is concerned.
  //
  // The cached copy (if any) is made current BEFORE the request, so the page paints from it and the
  // read behind it is a revalidation rather than a wait (#211). An answer that names no story —
  // a 404 on a deleted ongoing, an empty body — drops the cache with it, so a story that is gone
  // can never be served from memory.
  async getStory({ commit, state }, id) {
    commit('SET_RECENT', id)
    const cached = state.storyCache[id]
    if (cached) commit('SET_CURRENT_STORY', cached)
    try {
      const res = await api.getWithPathById('ongoing', id, 'story')
      const data = res?.data ?? null
      if (!data) commit('DROP_CACHED_STORY', id)
      commit('SET_CURRENT_STORY', data)
      return data
    } catch {
      commit('DROP_CACHED_STORY', id)
      commit('SET_CURRENT_STORY', null)
      return null
    }
  },

  clearCurrentStory({ commit }) {
    commit('SET_CURRENT_STORY', null)
  },

  // Fetch a Story's full data WITHOUT making it the current one — used to expand a sub-story inline
  // inside its parent's view (same route, expandable like a timeline step).
  async fetchStory(_ctx, id) {
    try {
      const res = await api.getWithPathById('ongoing', id, 'story')
      return res?.data ?? null
    } catch {
      return null
    }
  },

  // --- Smart Inbox: email → ongoing suggestions ---
  // Suggestions are produced server-side by timeline-srv when emails are ingested (real emails that
  // continue an ongoing are attached + bumped; others become suggested ongoings). The UI only READS them.
  // One read at a time: the layout's poll, a story open and a Home mount can all ask within the
  // same second, and while the previous answer is still in flight they share it instead of
  // stacking identical requests on the BFF.
  async fetchSuggestions({ commit }) {
    if (suggestionsInFlight) return suggestionsInFlight
    suggestionsInFlight = (async () => {
      try {
        const res = await api.get('ongoing/suggestions', {})
        commit('SET_SUGGESTIONS', Array.isArray(res?.data) ? res.data : [])
      } catch { /* best-effort — never blocks the UI */ }
      finally { suggestionsInFlight = null }
    })()
    return suggestionsInFlight
  },
  // The OPEN story's offers (busy-my-web#291): "Connect this email to this Ongoing". Read on every
  // open — the list is short, the endpoint is one query, and a stale offer block would ask about an
  // edge the user has already answered elsewhere. Best-effort: a story must open with or without it.
  async fetchOffers({ commit }, id) {
    if (!id) { commit('SET_OFFERS', []); return [] }
    try {
      const res = await api.getWithPathById('ongoing', id, OFFERS_PATH)
      const items = offerItems(res?.data)
      commit('SET_OFFERS', items)
      return items
    } catch {
      commit('SET_OFFERS', [])
      return []
    }
  },
  // Confirm a suggestion → create the real Ongoing (origin email), then refresh the list.
  async confirmSuggestion({ commit, dispatch }, id) {
    commit('REMOVE_SUGGESTION', id)
    const res = await api.insert(`ongoing/suggestions/${id}/confirm`, {})
    await dispatch('fetch')
    return res?.data ?? null
  },
  async dismissSuggestion({ commit }, id) {
    commit('REMOVE_SUGGESTION', id)
    try { await api.insert(`ongoing/suggestions/${id}/dismiss`, {}) } catch { /* ignore */ }
  },
  // A thread offered to an EXISTING ongoing (busy-timeline-srv#21). What is being answered is a
  // graph EDGE, not a story, so both answers go to relationships/{id} — "Add" confirms the link,
  // "Not related" rejects it, and the rejection is kept so the pair is never offered again.
  // The row leaves the list at once — the answer is the user's, and re-drawing an answered question
  // invites a second click. It answers FALSE when the call failed, and the caller that holds the row
  // puts it back (FollowUps' `answerThreadOffer` → RESTORE_SUGGESTION): a question that vanished
  // while the edge is still Suggested would have the user believe they answered it.
  async addSuggestedThread({ commit, dispatch }, relationshipId) {
    if (!relationshipId) return false
    commit('REMOVE_SUGGESTION', relationshipId)
    // The SAME edge may also be an offer inside the open story (busy-my-web#291) — one question, two
    // places it can be read, so one answer clears both.
    commit('REMOVE_OFFER', relationshipId)
    try {
      await api.insertWithPath('ongoing', confirmPath(relationshipId), {})
      // The ongoing gained a thread: its story (if open) and the list's order are both stale.
      dispatch('fetch')
      return true
    } catch {
      return false
    }
  },
  async rejectSuggestedThread({ commit }, relationshipId) {
    if (!relationshipId) return false
    commit('REMOVE_SUGGESTION', relationshipId)
    commit('REMOVE_OFFER', relationshipId)
    try {
      await api.insertWithPath('ongoing', rejectPath(relationshipId), {})
      return true
    } catch {
      return false
    }
  },
  // An ACTION offered for a thread (busy-timeline-srv#23): "create a task from this?", "mark as
  // waiting?", "add to <pack>?". Same shape as the thread answers — the row leaves at once and a
  // failed call answers null/false so the caller can put it back. "Do it" answers the server's
  // payload (which creation mode to open, prefilled — see storyAutomation.acceptedOpener).
  async acceptActionSuggestion({ commit }, id) {
    if (!id) return null
    commit('REMOVE_SUGGESTION', id)
    try {
      const res = await api.insertWithPath('suggestions', acceptActionPath(id), {})
      return res?.data ?? null
    } catch {
      return null
    }
  },
  async dismissActionSuggestion({ commit }, id) {
    if (!id) return false
    commit('REMOVE_SUGGESTION', id)
    try {
      await api.insertWithPath('suggestions', dismissActionPath(id), {})
      return true
    } catch {
      return false
    }
  },
  // "Dismiss" on a reply row (busy-timeline-srv#34). The row is one LINE but it may stand for
  // several replies, and each of those is its own suggestion server-side — so the answer is one
  // call per reply, and the row leaves once. Same discipline as the answers above: the caller puts
  // the row back when nothing reached the server.
  async dismissReplyReview({ commit }, row) {
    const ids = (row?.dismissIds?.length ? row.dismissIds : [row?.id]).filter(Boolean)
    if (!ids.length) return false
    commit('REMOVE_SUGGESTION', row.id)
    let answered = 0
    for (const id of ids) {
      try {
        await api.insertWithPath('suggestions', dismissActionPath(id), {})
        answered += 1
      } catch { /* the next one may still land */ }
    }
    return answered > 0
  },
  // Ask the engine about ONE thread now (busy-timeline-srv#23): the BFF adds the semantic signal
  // and timeline-srv scores the rest. Best-effort, then the list is re-read — the offers it made
  // are what the strip shows.
  async evaluateThread({ dispatch }, timelineId) {
    if (!timelineId) return null
    try {
      const res = await api.insertWithPath('suggestions', 'evaluate', { timelineId })
      dispatch('fetchSuggestions')
      return res?.data ?? null
    } catch {
      return null
    }
  },
  // Open an email as an ongoing (create-or-reuse). Given the email's timeline, the server returns
  // the existing ongoing for that thread if there is one (confirming a pending suggestion), else it
  // starts a fresh email-origin ongoing — never a duplicate. Returns the story (has `.id`).
  async openFromTimeline({ dispatch }, timelineId) {
    if (!timelineId) return null
    const res = await api.insert(`ongoing/from-timeline/${timelineId}`, {})
    await dispatch('fetch') // sidebar + list may have gained an ongoing
    return res?.data ?? null
  },

  // Delete ONE saved research item (photo / snippet / link) — the UI drops it IMMEDIATELY.
  async removeResearchItem({ commit }, { id, itemId }) {
    commit('REMOVE_RESEARCH_ITEM', { id, itemId })
    await api.deleteWithPath('ongoing', id, `research/${itemId}`)
    return true
  },

  // Delete ONE timeline event (a task/note/event step) — its entity was deleted, so the step and
  // the Focus lines it created leave the story too (Pouria, 2026-08-14). Optimistic, same as
  // removeResearchItem.
  async removeEvent({ commit }, { id, eventId }) {
    commit('REMOVE_EVENT', { id, eventId })
    await api.deleteWithPath('ongoing', id, `events/${eventId}`)
    return true
  },

  // Edit a saved research item IN PLACE — the store (and every view on it) updates IMMEDIATELY.
  async updateResearchItem({ commit }, { id, itemId, title, content }) {
    commit('PATCH_RESEARCH_ITEM', { id, itemId, title, content })
    await api.updateWithPath('ongoing', id, `research/${itemId}`, { title, content })
    return true
  },

  // Edit a timeline event IN PLACE — same instant, optimistic update.
  async updateEvent({ commit }, { id, eventId, label, detail }) {
    commit('PATCH_EVENT', { id, eventId, label, detail })
    await api.updateWithPath('ongoing', id, `events/${eventId}`, { label, detail })
    return true
  },

  // Tick a Focus checkbox: mark a task/event STEP done (or un-done) on the story timeline —
  // outstanding steps are the ones Focus shows (MVP Stage 4).
  async completeStep(_, { id, eventId, done = true }) {
    await api.updateWithPath('ongoing', id, `events/${eventId}`, { isDone: done })
    return true
  },

  // Archive an Ongoing (busy-my-web#409, the close-out's "Archive the story"). The plain update the
  // server has always accepted (`IsArchived` on UpdateOngoingRequest) — the advance endpoint ignores
  // a manualStatus of "archived" by design, so this is the one door. The list is re-read because
  // an archived story leaves it.
  async archive({ dispatch }, id) {
    if (!id) return null
    const res = await api.update('ongoing', id, { isArchived: true })
    await dispatch('fetch')
    return res?.data ?? null
  },

  // Rename an Ongoing. OPTIMISTIC: the sidebar row and the open story show the new name immediately;
  // a failed request re-syncs from the server and rethrows so the UI can say it failed.
  async rename({ dispatch, commit }, { id, title }) {
    if (!id || !title) return null
    commit('RENAME_ITEM', { id, title })
    try {
      const res = await api.update('ongoing', id, { title })
      dispatch('fetch') // refresh sidebar + parents' children in the background
      return res?.data ?? null
    } catch (e) {
      dispatch('fetch') // roll back to the server's truth
      throw e
    }
  },

  // Delete an Ongoing (the whole story). Drop it from the list IMMEDIATELY (optimistic) so the sidebar
  // updates without waiting on the server, then delete on the server and re-sync in the background.
  async remove({ dispatch, commit }, id) {
    commit('REMOVE_ITEM', id)
    // A deleted ongoing must not stay pinned — the pin now outlives the session (#206), so a stale
    // id would sit in storage waiting to hoist a row that no longer exists.
    commit('RELEASE_RECENT', id)
    commit('SET_CURRENT_STORY', null)
    // The delete also clears the story's tasks/events/notes (busy-my-web#55) and answers with the
    // member timelines it emptied. Strip those rows out of the Global timeline panel RIGHT NOW so it
    // never shows a deleted ongoing's task for the length of a round-trip; the fetch after it is the
    // reconciliation. An older BFF (or a failed call) names no ids and the fetch alone does the work.
    const res = await api.delete('ongoing', id)
    commit('globalTimeline/REMOVE_BY_TIMELINE_IDS', orphanedTimelineIds(res), { root: true })
    dispatch('fetch')
    dispatch('globalTimeline/fetch', null, { root: true })
  },

  // Rule 4: saving selected research auto-creates an Ongoing.
  // `packTitle` (2026-09-15 parity audit E-2, busy-my-web#476): the ONGOING is named after the
  // search, the PACK after whatever was typed in the naming card — two different names for two
  // different things. It is sent alongside the query and the server names the first pack with it
  // when it understands the field; without it the pack keeps the query's name, which is what it has
  // always been and is still right when nothing was typed.
  async createFromResearch({ dispatch, commit, rootGetters }, { query, items, packTitle }) {
    const mapped = researchSaveItems(items, query)
    if (rootGetters['user/isAuthenticated']) persistResearchNotes(items, query)
    let res
    try {
      res = await api.insert('ongoing/from-research', {
        query: query || '',
        items: mapped,
        ...(packTitle && packTitle !== query ? { packTitle } : {}),
      })
    } catch (e) {
      // Guest quota reached — the next ongoing needs a real account.
      if (isGuestLimit(e)) { loginForSave(); return new Promise(() => {}) }
      throw e
    }
    const data = res?.data ?? null
    // Seed the current story so its page can render instantly (no blank screen while it re-fetches).
    if (data) commit('SET_CURRENT_STORY', data)
    if (data?.id) commit('SET_RECENT', data.id)
    // Refresh the sidebar list in the background — don't make the user wait on it before navigating.
    dispatch('fetch')
    return data
  },

  // Start an Ongoing from a created entity (task / note / event) — origin drives its icon.
  // The entity's own timeline is LINKED, so the story actually knows what it is made of: its status
  // follows that task's due date, and deleting the task empties (hides) the ongoing with it.
  async createFromEntity({ dispatch, commit }, { title, story, origin, timelineId }) {
    let res
    try {
      res = await api.insert('ongoing', {
        title: title || 'New',
        story: story || '',
        origin: origin || 'task',
        ...(timelineId ? { timelineIds: [timelineId] } : {}),
      })
    } catch (e) {
      if (isGuestLimit(e)) return null // quota — the entity itself is saved; just no new story
      throw e
    }
    if (res?.data?.id) commit('SET_RECENT', res.data.id)
    await dispatch('fetch')
    return res?.data ?? null
  },

  // Create a sub-ongoing nested under a parent (a major story with sub-stories).
  //
  // Deliberately does NOT mark the new story as `recentId` (#33): the sidebar's
  // top-level list only holds parents, and a sub-story hoisted out of its family
  // would break the grouping. It already appears under its parent, in the
  // creation order that list is sorted by.
  async createSub({ dispatch }, { parentOngoingId, title, story, origin }) {
    let res
    try {
      res = await api.insert('ongoing', {
        title: title || 'Sub-story',
        story: story || '',
        origin: origin || 'note',
        parentOngoingId,
      })
    } catch (e) {
      if (isGuestLimit(e)) { loginForSave(); return new Promise(() => {}) }
      throw e
    }
    await dispatch('fetch')
    return res?.data ?? null
  },

  // Create a sub-ongoing BY NAME, through the endpoint that exists for it
  // (busy-timeline-srv#12: POST v1/ongoing/{id}/sub → the CHILD's story).
  //
  // Not `createSub` above, and the two are deliberately left side by side: `createSub` posts a whole
  // ongoing with a parent id on it and is what the SmartBox's "New sub-ongoing" draft still uses
  // (the sub is born out of a note/task/event). This one asks the parent to make a child — the
  // server writes the child's story line, gives it its origin and adds the parent's permanent
  // receipt step, none of which the generic create does. Which of the two survives is Saffet's open
  // question on the issue, so neither was changed to fit the other.
  //
  // The error is RE-THROWN rather than swallowed: the guards answer 400 with a sentence meant for
  // the user, and only the caller knows where to show it (see lib/subOngoing.createError).
  // The story is NOT made current — the parent's page is still the one being read.
  async createSubOngoing({ dispatch }, { parentOngoingId, name }) {
    let res
    try {
      res = await api.insertWithPath('ongoing', `${parentOngoingId}/sub`, { name: String(name ?? '') })
    } catch (e) {
      if (isGuestLimit(e)) { loginForSave(); return new Promise(() => {}) }
      throw e
    }
    // The sidebar's list gains the child (it hangs under its parent there already).
    dispatch('fetch')
    return res?.data ?? null
  },
}

// Busy guest mode: the FULL ongoing flow works signed-out against localStorage (the guest
// fallbacks above) — only the email-dependent actions still require a real account, since a
// guest has no mailbox to link threads from.
gateActionsBehindLogin(actions, [
  "linkTimeline",
  "confirmSuggestion",
  // Adding an offered thread links real mail to a real ongoing — same account requirement.
  "addSuggestedThread",
  // Doing something WITH a thread is about real mail too (busy-timeline-srv#23).
  "acceptActionSuggestion",
]);

// How many opened stories to keep (#211). Enough that moving between the handful of ongoings you
// are actually working on is instant, small enough that a long session cannot hold a list-full of
// heavy aggregates. Eviction is by FIRST SEEN, which is all a cap of this size needs.
export const STORY_CACHE_MAX = 12

// Remember a story as the last known state of that ongoing. Anything without an id (a null current
// story, a shell) is not a story and is never cached.
function cacheStory(s, story) {
  if (!story?.id) return
  const next = { ...s.storyCache, [story.id]: story }
  const keys = Object.keys(next)
  if (keys.length > STORY_CACHE_MAX) delete next[keys[0]]
  s.storyCache = next
}

export const mutations = {
  SET_RECENT(s, id) {
    s.recentId = id || null
    writePinnedId(s.recentId)
  },
  // Drops the pin: with no id, whatever is pinned; with one, only if that IS the pinned story
  // (deleting some other ongoing must not move the top of the list).
  RELEASE_RECENT(s, id) {
    if (!id || s.recentId === id) {
      s.recentId = null
      writePinnedId(null)
    }
  },
  // A CHANGED kind drops the pack selection with it (#56): `packId` belongs to the packs filter —
  // carried into a tasks or notes filter it would silently narrow that one to a single pack, which
  // reads as missing steps. A patch that names `packId` itself still wins (selecting a pack sends
  // both at once).
  SET_STORY_FILTERS(s, patch) {
    const p = patch || {}
    const kindChanged = 'kind' in p && p.kind !== s.storyFilters.kind
    s.storyFilters = { ...s.storyFilters, ...(kindChanged ? { packId: null } : {}), ...p }
  },
  SET_PACK_VIEW(s, v) { s.packView = v },
  SET_PACK_SELECTION(s, v) { s.packSelection = v || null },
  RESET_STORY_FILTERS(s) { s.storyFilters = { kind: null, packId: null } },
  SET_COVERED_STORY(s, id) { s.coveredStoryId = id || null },
  SET_ITEMS(s, v) {
    const list = Array.isArray(v) ? v : []
    // Forget deletions older than 90s (past the 60s list cache) so they can't linger; suppress the rest
    // regardless of fetch ordering, so a stale/out-of-order refetch can't resurrect a deleted ongoing.
    const cutoff = Date.now() - 90 * 1000
    s.deletedIds = s.deletedIds.filter(d => d.at > cutoff)
    const ids = s.deletedIds.map(d => d.id)
    s.items = list.filter(o => !ids.includes(o.id) && !ids.includes(o.parentOngoingId))
  },
  // Drop an ongoing and any of its sub-stories from the list right away (optimistic delete), and
  // remember the id (with a timestamp) so a stale refetch can't bring it back.
  REMOVE_ITEM(s, id) {
    if (!s.deletedIds.some(d => d.id === id)) s.deletedIds = [...s.deletedIds, { id, at: Date.now() }]
    s.items = s.items.filter(o => o.id !== id && o.parentOngoingId !== id)
    // …including out of the story cache: a deleted ongoing must not be paintable from memory.
    const kids = Object.keys(s.storyCache).filter(k => k === id || s.storyCache[k]?.parentOngoingId === id)
    if (kids.length) {
      const next = { ...s.storyCache }
      for (const k of kids) delete next[k]
      s.storyCache = next
    }
  },
  // Same tombstone, for ongoings the SERVER hid (their last entity was deleted) — the list is already
  // without them, we only need the stale-refetch guard.
  TOMBSTONE_ITEMS(s, ids) {
    const fresh = (ids || []).filter(id => id && !s.deletedIds.some(d => d.id === id))
    if (fresh.length) s.deletedIds = [...s.deletedIds, ...fresh.map(id => ({ id, at: Date.now() }))]
  },
  // Restoring the entity un-hides the ongoing: drop its tombstone so it isn't suppressed on the way back.
  FORGET_DELETED(s, ids) {
    if (!ids || !ids.length) return
    s.deletedIds = s.deletedIds.filter(d => !ids.includes(d.id))
  },
  // Optimistic rename — the sidebar row AND the open story pick the new name up instantly.
  RENAME_ITEM(s, { id, title }) {
    s.items = s.items.map(o => (o.id === id ? { ...o, title } : o))
    if (s.currentStory?.id === id) s.currentStory = { ...s.currentStory, title }
    if (s.storyCache[id]) cacheStory(s, { ...s.storyCache[id], title })
  },
  // Optimistic edits on the loaded story — the timeline/panel/tabs reflect changes instantly.
  PATCH_RESEARCH_ITEM(s, { id, itemId, title, content }) {
    if (s.currentStory?.id !== id) return
    s.currentStory = {
      ...s.currentStory,
      research: (s.currentStory.research || []).map(r => (r.id === itemId
        ? { ...r, ...(title !== undefined && { title }), ...(content !== undefined && { content }) }
        : r)),
    }
    cacheStory(s, s.currentStory)
  },
  PATCH_EVENT(s, { id, eventId, label, detail }) {
    if (s.currentStory?.id !== id) return
    s.currentStory = {
      ...s.currentStory,
      timeline: (s.currentStory.timeline || []).map(e => (e.id === eventId
        ? { ...e, ...(label !== undefined && { label }), ...(detail !== undefined && { detail }) }
        : e)),
    }
    cacheStory(s, s.currentStory)
  },
  REMOVE_RESEARCH_ITEM(s, { id, itemId }) {
    if (s.currentStory?.id !== id) return
    s.currentStory = { ...s.currentStory, research: (s.currentStory.research || []).filter(r => r.id !== itemId) }
    cacheStory(s, s.currentStory)
  },
  REMOVE_EVENT(s, { id, eventId }) {
    if (s.currentStory?.id !== id) return
    s.currentStory = { ...s.currentStory, timeline: (s.currentStory.timeline || []).filter(e => e.id !== eventId) }
    cacheStory(s, s.currentStory)
  },
  SET_LOADED(s, v) { s.loaded = v },
  SET_CURRENT_STORY(s, v) {
    s.currentStory = v
    cacheStory(s, v)
  },
  DROP_CACHED_STORY(s, id) {
    if (!id || !(id in s.storyCache)) return
    const next = { ...s.storyCache }
    delete next[id]
    s.storyCache = next
  },
  SET_SUGGESTIONS(s, v) { s.suggestions = v },
  REMOVE_SUGGESTION(s, id) { s.suggestions = s.suggestions.filter(x => x.id !== id) },
  // Put an offer back after its answer failed to reach the server (busy-timeline-srv#21). Ignores a
  // row that is already there, so a double restore cannot duplicate the question.
  RESTORE_SUGGESTION(s, item) {
    if (!item?.id || s.suggestions.some(x => x.id === item.id)) return
    s.suggestions = [...s.suggestions, item]
  },
  // The open story's offers (busy-my-web#291). Same three verbs, same discipline as above: a row
  // leaves the moment it is answered and comes back if the answer never reached the server.
  SET_OFFERS(s, v) { s.offers = Array.isArray(v) ? v : [] },
  REMOVE_OFFER(s, relationshipId) {
    if (!relationshipId) return
    s.offers = s.offers.filter(x => String(x?.relationshipId || x?.id) !== String(relationshipId))
  },
  RESTORE_OFFER(s, item) {
    const id = item?.relationshipId || item?.id
    if (!id || s.offers.some(x => String(x?.relationshipId || x?.id) === String(id))) return
    s.offers = [...s.offers, item]
  },
}
